import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PaymentSetDefaultError,
  promoteDefault,
  requestSetDefaultPaymentMethod,
  type SavedPaymentMethodsData,
  savedPaymentMethodsKey,
} from './paymentApi';

interface UseSetDefaultPaymentMethodOptions {
  onSuccess?: () => void;
}

interface SetDefaultContext {
  previous: SavedPaymentMethodsData | undefined;
}

/**
 * Sets a saved credit card or bank account as the member's default. Optimistic — unlike the other
 * mutations, this fires with no confirmation dialog and no menu left open to carry a pending state
 * (the action sheet closes on select), so the Default badge has to move instantly for the
 * interaction to feel responsive. Rolled back on failure; NOT_FOUND is the only failure code, and
 * the target is already visible in the list, so a rollback is rare in practice.
 */
export function useSetDefaultPaymentMethod({ onSuccess }: UseSetDefaultPaymentMethodOptions = {}) {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation<{ id: string; isDefault: boolean }, unknown, string, SetDefaultContext>({
    mutationFn: (id: string) => requestSetDefaultPaymentMethod(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: savedPaymentMethodsKey });
      const previous = queryClient.getQueryData<SavedPaymentMethodsData>(savedPaymentMethodsKey);
      if (previous) {
        queryClient.setQueryData<SavedPaymentMethodsData>(savedPaymentMethodsKey, promoteDefault(previous, id));
      }
      return { previous };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savedPaymentMethodsKey });
      onSuccess?.();
    },
    onError: (error: unknown, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(savedPaymentMethodsKey, context.previous);
      }
      // PaymentSetDefaultError wraps setDefaultPaymentMethod's Failure variant (NOT_FOUND only).
      const code = error instanceof PaymentSetDefaultError ? error.code : extractGqlErrorCode(error);
      if (error instanceof Error) {
        trackException(error, { source: 'setDefaultPaymentMethod', errorCode: code });
      }
      toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
    },
  });

  return {
    submit: (id: string) => mutation.mutate(id),
    isPending: mutation.isPending,
    pendingId: mutation.isPending ? mutation.variables : undefined,
  };
}
